import { ensureMlsStorageSchema } from './messengerV2StorageSchema.js';
import { isMessengerV2DebugLoggingEnabled } from './messengerV2Flags.js';

let workerSource = 'uninitialized';
let workerSourceUrl = null;
let workerAssetUrlPromise = null;

const DEFAULT_WORKER_CALL_TIMEOUT_MS = 15000;
const INIT_WORKER_CALL_TIMEOUT_MS = 60000;

const loadWorkerAssetUrl = () => {
  if (!workerAssetUrlPromise) {
    workerAssetUrlPromise = import('./messengerV2WorkerAsset.js')
      .then((module) => module?.workerUrl ?? module?.default ?? null)
      .catch((error) => {
        if (typeof console?.warn === 'function') {
          try {
            console.warn('[MLS] failed loading V2 worker asset url', error);
          } catch {}
        }
        return null;
      });
  }
  return workerAssetUrlPromise;
};

class StubWorker {
  constructor() {
    this.listeners = new Map();
  }

  addEventListener(type, listener) {
    if (!type || typeof listener !== 'function') return;
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type).add(listener);
  }

  removeEventListener(type, listener) {
    if (!type || typeof listener !== 'function') return;
    const bucket = this.listeners.get(type);
    if (bucket) {
      bucket.delete(listener);
      if (bucket.size === 0) {
        this.listeners.delete(type);
      }
    }
  }

  postMessage() {
    throw new Error('MLS worker is unavailable in this environment');
  }

  terminate() {
    this.listeners.clear();
  }
}

let worker = null;
let nextId = 0;
const pending = new Map();
let storageReadyPromise = null;

const ensureStorageReady = () => {
  if (!storageReadyPromise) {
    storageReadyPromise = ensureMlsStorageSchema().catch((error) => {
      storageReadyPromise = null;
      if (typeof console?.warn === 'function') {
        try {
          console.warn('[MLS] Failed ensuring storage schema, continuing with best effort', error);
        } catch {}
      }
      return null;
    });
  }
  return storageReadyPromise;
};

const createWorkerRestartedError = () => {
  const error = new Error('mls_worker_restarted');
  error.code = 'MLS_WORKER_RESTARTED';
  return error;
};

const createWorkerFallbackFailedError = () => {
  const error = new Error('mls_worker_fallback_failed');
  error.code = 'MLS_WORKER_FALLBACK_FAILED';
  return error;
};

const clearPendingEntryTimeout = (entry) => {
  if (!entry || !entry.timeoutId) return;
  try {
    clearTimeout(entry.timeoutId);
  } catch {}
  entry.timeoutId = null;
};

const rejectAllPending = (error) => {
  if (pending.size === 0) return;
  const entries = Array.from(pending.values());
  pending.clear();
  entries.forEach((entry) => {
    clearPendingEntryTimeout(entry);
    try {
      entry?.reject?.(error);
    } catch {}
  });
};

const normalizeWorkerError = (error = null) => {
  let message = 'mls_worker_error';
  const metadata = {};

  if (error && typeof error === 'object') {
    Object.assign(metadata, error);
    if (typeof error.message === 'string' && error.message) {
      message = error.message;
    } else if (typeof error.code === 'string' && error.code) {
      message = error.code;
    }
  } else if (typeof error === 'string' && error) {
    message = error;
  }

  const workerError = new Error(message);
  Object.entries(metadata).forEach(([key, value]) => {
    if (key !== 'message') {
      workerError[key] = value;
    }
  });
  return workerError;
};

const handleWorkerMessage = (event) => {
  if (event?.data?.type === 'MLS_WORKER_ERROR') {
    console.error('[MLS main] worker reported error', {
      error: event.data.err,
      source: workerSource,
      url: workerSourceUrl,
    });
    return;
  }

  const { id, status, result, error } = event?.data || {};
  if (!id) return;
  const entry = pending.get(id);
  if (!entry) return;

  pending.delete(id);
  clearPendingEntryTimeout(entry);
  if (status === 'ok') {
    entry.resolve(result);
    return;
  }

  entry.reject(normalizeWorkerError(error));
};

const handleWorkerMessageError = (event) => {
  console.error('[MLS main] worker.onmessageerror', {
    event,
    source: workerSource,
    url: workerSourceUrl,
  });
};

const instantiateWorker = async (options = {}) => {
  if (typeof Worker === 'undefined' && typeof SharedWorker === 'undefined') {
    return null;
  }

  try {
    const workerUrl = await loadWorkerAssetUrl();
    if (!workerUrl) {
      return null;
    }
    if (typeof SharedWorker !== 'undefined') {
      const sharedWorker = new SharedWorker(workerUrl, {
        type: 'module',
        name: 'nettgrav-messenger-v2-mls',
        ...options,
      });
      const instance = sharedWorker.port;
      instance.start?.();
      instance.__nettgravSharedWorker = sharedWorker;
      instance.terminate = () => instance.close?.();
      workerSource = 'shared-module-url';
      workerSourceUrl = String(workerUrl);
      if (isMessengerV2DebugLoggingEnabled() && typeof console?.debug === 'function') {
        try {
          console.debug('[MLS] shared worker connected', {
            strategy: workerSource,
            url: workerSourceUrl,
          });
        } catch {}
      }
      return instance;
    }
    const instance = new Worker(workerUrl, { type: 'module', ...options });
    workerSource = 'module-url';
    workerSourceUrl = String(workerUrl);
    if (isMessengerV2DebugLoggingEnabled() && typeof console?.debug === 'function') {
      try {
        console.debug('[MLS] worker instantiated', {
          strategy: workerSource,
          url: workerSourceUrl,
        });
      } catch {}
    }
    return instance;
  } catch (error) {
    if (isMessengerV2DebugLoggingEnabled() && typeof console?.debug === 'function') {
      try {
        console.debug('[MLS] worker instantiation failed', {
          strategy: 'module-url',
          error,
        });
      } catch {}
    }
    return null;
  }
};

const attachWorkerEventListeners = (instance) => {
  if (!instance) return;
  if (typeof instance.addEventListener === 'function') {
    instance.addEventListener('message', handleWorkerMessage);
  }
  if ('onerror' in instance || typeof instance.onerror === 'undefined') {
    instance.onerror = handleWorkerError;
  }
  if ('onmessageerror' in instance || typeof instance.onmessageerror === 'undefined') {
    instance.onmessageerror = handleWorkerMessageError;
  }
  const sharedWorker = instance.__nettgravSharedWorker;
  if (sharedWorker && ('onerror' in sharedWorker || typeof sharedWorker.onerror === 'undefined')) {
    sharedWorker.onerror = handleWorkerError;
  }
};

const ensureWorker = async () => {
  if (worker) return worker;

  const instance = await instantiateWorker();
  if (instance) {
    worker = instance;
    attachWorkerEventListeners(worker);
    if (isMessengerV2DebugLoggingEnabled() && typeof console?.info === 'function') {
      try {
        console.info('[MLS] worker started', {
          source: workerSource,
          url: workerSourceUrl,
        });
      } catch {}
    }
    return worker;
  }

  workerSource = 'stub';
  workerSourceUrl = null;
  worker = new StubWorker();
  return worker;
};

const handleWorkerError = (event) => {
  const details = {
    message: event?.message || null,
    filename: event?.filename || null,
    lineno: event?.lineno || null,
    colno: event?.colno || null,
    source: workerSource,
    url: workerSourceUrl,
  };
  if (event?.error) {
    details.error = event.error;
    details.stack = event.error?.stack;
  }
  console.error('[MLS main] worker.onerror', details);
  try {
    console.warn('[MLS] worker restarting after error', {
      source: workerSource,
      url: workerSourceUrl,
      message: details.message,
    });
  } catch {}

  const previousWorker = worker;
  worker = null;
  if (previousWorker && typeof previousWorker.terminate === 'function') {
    try {
      previousWorker.terminate();
    } catch {}
  }

  workerSource = 'stub';
  workerSourceUrl = null;
  worker = new StubWorker();
  rejectAllPending(createWorkerFallbackFailedError());
};

const restartWorker = async () => {
  try {
    const previousWorker = worker;
    worker = null;
    if (previousWorker && typeof previousWorker.terminate === 'function') {
      previousWorker.terminate();
    }
  } catch {}

  const replacement = await instantiateWorker();
  if (replacement) {
    worker = replacement;
    attachWorkerEventListeners(worker);
    return true;
  }

  workerSource = 'stub';
  workerSourceUrl = null;
  worker = new StubWorker();
  return false;
};

export function callMlsWorker(command, payload, options = {}) {
  return ensureStorageReady().then(async () => {
    const instance = await ensureWorker();
    const id = `mls-${++nextId}`;

    return new Promise((resolve, reject) => {
      const timeoutMs = (() => {
        const candidate = Number(options?.timeoutMs);
        if (Number.isFinite(candidate) && candidate > 0) {
          return Math.max(250, Math.trunc(candidate));
        }
        if (String(command || '').trim().toLowerCase() === 'init') {
          return INIT_WORKER_CALL_TIMEOUT_MS;
        }
        return DEFAULT_WORKER_CALL_TIMEOUT_MS;
      })();

      const timeoutEntry = { resolve, reject, timeoutId: null };
      pending.set(id, timeoutEntry);

      if (Number.isFinite(timeoutMs) && timeoutMs > 0) {
        timeoutEntry.timeoutId = setTimeout(() => {
          const entry = pending.get(id);
          if (!entry) return;
          pending.delete(id);
          clearPendingEntryTimeout(entry);
          const timeoutError = new Error(
            `MLS worker call timed out (${String(command)} after ${timeoutMs}ms)`,
          );
          timeoutError.code = 'MLS_WORKER_CALL_TIMEOUT';
          timeoutError.command = command;
          timeoutError.timeoutMs = timeoutMs;
          entry.reject(timeoutError);

          if (options?.restartOnTimeout === true) {
            restartWorker().catch(() => {});
            rejectAllPending(createWorkerRestartedError());
          }
        }, timeoutMs);
      }

      const message = { id, command, payload };
      const transferSet = new Set();
      if (payload && typeof payload === 'object') {
        const pushTransfer = (value) => {
          if (!value) return;
          if (value instanceof ArrayBuffer) {
            transferSet.add(value);
            return;
          }
          if (ArrayBuffer.isView(value) && value.buffer) {
            transferSet.add(value.buffer);
          }
        };

        pushTransfer(payload.welcome);
        pushTransfer(payload.ratchetTree);
        pushTransfer(payload.ratchetTreeBytes);
        pushTransfer(payload.ratchet_tree_bytes);
        pushTransfer(payload.ratchetTreeBuffer);
        pushTransfer(payload.ratchet_tree_buffer);
      }

      if (transferSet.size > 0) {
        instance.postMessage(message, Array.from(transferSet));
      } else {
        instance.postMessage(message);
      }
    });
  });
}

export function terminateMlsWorker() {
  if (worker) {
    worker.terminate();
    worker = null;
    rejectAllPending(createWorkerRestartedError());
  }
}
